/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React from 'react';
import { useTheme } from '../../theme/ThemeContext';

const StandardizationVisual = ({ primaryColor, secondaryColor }) => (
    <svg viewBox="0 0 850 340" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: 'auto', maxWidth: '850px' }}>
        <defs>
            <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill="#ccc" />
            </marker>
            <marker id="arrowhead-primary" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill={primaryColor} />
            </marker>
            <linearGradient id="chaosGradient" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#f8f8f8" />
                <stop offset="100%" stopColor="#eeeeee" />
            </linearGradient>
            <linearGradient id="arceeContainerGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={primaryColor} stopOpacity="0.03" />
                <stop offset="100%" stopColor={primaryColor} stopOpacity="0.08" />
            </linearGradient>
            <linearGradient id="coreGradient" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor={primaryColor} />
                <stop offset="100%" stopColor={secondaryColor} />
            </linearGradient>
            <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#000" floodOpacity="0.08" />
            </filter>
        </defs>

        {/* --- LEFT SIDE: LEGACY CHAOS --- */}
        <g transform="translate(50, 40)">
            <text x="160" y="-10" textAnchor="middle" fill="#999" fontWeight="700" fontSize="13" letterSpacing="1">LEGACY CHAOS</text>

            {/* Background splatter to indicate mess */}
            <circle cx="160" cy="130" r="140" fill="#fcfcfc" stroke="#f0f0f0" strokeDasharray="5 5" />

            {/* Connection Lines (Messy) */}
            <path d="M60,60 Q160,160 260,60" fill="none" stroke="#e0e0e0" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
            <path d="M60,190 Q110,90 160,60" fill="none" stroke="#e0e0e0" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
            <path d="M260,190 Q210,90 60,60" fill="none" stroke="#e0e0e0" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
            <path d="M160,230 Q160,160 260,190" fill="none" stroke="#e0e0e0" strokeWidth="1.5" markerEnd="url(#arrowhead)" />
            <path d="M50,120 L270,120" fill="none" stroke="#e0e0e0" strokeWidth="1.5" markerEnd="url(#arrowhead)" strokeDasharray="4 2" />

            {/* Nodes scattered */}
            <g transform="translate(60,60)">
                <circle r="30" fill="#fff" stroke="#ddd" strokeWidth="2" filter="url(#softShadow)" />
                <text y="5" textAnchor="middle" fontSize="10" fill="#777" fontWeight="600">App A</text>
            </g>

            <g transform="translate(260,60)">
                <circle r="30" fill="#fff" stroke="#ddd" strokeWidth="2" filter="url(#softShadow)" />
                <text y="5" textAnchor="middle" fontSize="10" fill="#777" fontWeight="600">App B</text>
            </g>

            <g transform="translate(130,210)">
                <rect x="-30" y="-20" width="60" height="40" rx="4" fill="#fff" stroke="#ddd" strokeWidth="2" filter="url(#softShadow)" />
                <text y="5" textAnchor="middle" fontSize="10" fill="#777" fontWeight="600">DB 1</text>
            </g>

            <g transform="translate(230,170)">
                <rect x="-30" y="-20" width="60" height="40" rx="4" fill="#fff" stroke="#ddd" strokeWidth="2" filter="url(#softShadow)" />
                <text y="5" textAnchor="middle" fontSize="10" fill="#777" fontWeight="600">DB 2</text>
            </g>

            <g transform="translate(60,190)">
                <rect x="-25" y="-25" width="50" height="50" rx="25" fill="#fff" stroke="#ddd" strokeWidth="2" filter="url(#softShadow)" />
                <text y="5" textAnchor="middle" fontSize="10" fill="#777" fontWeight="600">Auth</text>
            </g>
        </g>

        {/* --- TRANSITION ARROW --- */}
        <g transform="translate(390, 150)">
            <path d="M0,0 L50,0" stroke={primaryColor} strokeWidth="3" markerEnd="url(#arrowhead-primary)" strokeLinecap="round" />
        </g>


        {/* --- RIGHT SIDE: ARCEE STANDARD --- */}
        <g transform="translate(480, 40)">
            <text x="160" y="-10" textAnchor="middle" fill={primaryColor} fontWeight="800" fontSize="13" letterSpacing="1">ARCEE STANDARD</text>

            {/* Unified Platform Container */}
            <rect x="20" y="20" width="280" height="230" rx="16" fill="url(#arceeContainerGradient)" stroke={primaryColor} strokeWidth="1" strokeOpacity="0.3" filter="url(#softShadow)" />

            {/* --- Stacked Layers --- */}

            {/* 1. Multiple Apps Layer */}
            <g transform="translate(40, 45)">
                {/* App 1 */}
                <rect x="0" y="0" width="70" height="50" rx="6" fill="#fff" stroke={primaryColor} strokeWidth="1.5" />
                <text x="35" y="30" textAnchor="middle" fill={primaryColor} fontWeight="700" fontSize="11">App A</text>

                {/* App 2 */}
                <rect x="85" y="0" width="70" height="50" rx="6" fill="#fff" stroke={primaryColor} strokeWidth="1.5" />
                <text x="120" y="30" textAnchor="middle" fill={primaryColor} fontWeight="700" fontSize="11">App B</text>

                {/* App 3 */}
                <rect x="170" y="0" width="70" height="50" rx="6" fill="#fff" stroke={primaryColor} strokeWidth="1.5" />
                <text x="205" y="30" textAnchor="middle" fill={primaryColor} fontWeight="700" fontSize="11">App C</text>
            </g>

            {/* Connection to API layer */}
            <path d="M75,95 L75,115" stroke={primaryColor} strokeWidth="1.5" markerEnd="url(#arrowhead-primary)" />
            <path d="M160,95 L160,115" stroke={primaryColor} strokeWidth="1.5" markerEnd="url(#arrowhead-primary)" />
            <path d="M245,95 L245,115" stroke={primaryColor} strokeWidth="1.5" markerEnd="url(#arrowhead-primary)" />


            {/* 2. SaaS APIs Layer */}
            <g transform="translate(40, 115)">
                <rect width="240" height="40" rx="6" fill="#fff" stroke={secondaryColor} strokeWidth="1.5" />
                <text x="120" y="25" textAnchor="middle" fill={secondaryColor} fontWeight="700" fontSize="12" letterSpacing="0.5">UNIFIED API GATEWAY</text>
            </g>

            {/* Connection to Core */}
            <path d="M160,155 L160,175" stroke={secondaryColor} strokeWidth="1.5" markerEnd="url(#arrowhead-primary)" />

            {/* 3. Backend Core & DB Layer */}
            <g transform="translate(40, 175)">
                <rect width="240" height="55" rx="8" fill="url(#coreGradient)" stroke={primaryColor} strokeWidth="0" />
                <text x="120" y="22" textAnchor="middle" fill="#fff" fontWeight="800" fontSize="13">ARCEE CORE</text>
                <text x="120" y="42" textAnchor="middle" fill="rgba(255,255,255,0.9)" fontWeight="500" fontSize="11">+ Scalable & Secure DB</text>
            </g>

            {/* Side Labels */}
            <g transform="translate(320, 0)">
                <text x="0" y="70" fill={primaryColor} fontSize="11" fontWeight="600">Frontend Agnostic</text>
                <line x1="-10" y1="70" x2="-20" y2="70" stroke={primaryColor} strokeWidth="1" />

                <text x="0" y="140" fill={secondaryColor} fontSize="11" fontWeight="600">Auto-Generated</text>
                <line x1="-10" y1="135" x2="-20" y2="135" stroke={secondaryColor} strokeWidth="1" />

                <text x="0" y="200" fill={primaryColor} fontSize="11" fontWeight="600">Managed Backend</text>
                <line x1="-10" y1="200" x2="-20" y2="200" stroke={primaryColor} strokeWidth="1" />
            </g>

        </g>
    </svg>
);

function StandardizeSection() {
    const { theme } = useTheme();

    const features = [
        {
            icon: 'bi-code-slash',
            title: 'Open Source Core',
            description: 'Built on standard Django & React. Extend code anytime.'
        },
        {
            icon: 'bi-database',
            title: 'One Backend, Many Apps',
            description: "Don't manage 50 databases. Manage one arcee instance."
        },
        {
            icon: 'bi-robot',
            title: 'Chat Assistant',
            description: 'Chat with arcee to build your flows instantly.'
        }
    ];

    return (
        <section className="standardize-section" style={{ padding: '100px 0', background: '#fcfcfc', borderTop: '1px solid #f0f0f0' }}>
            <div className="container">
                <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                    <h2 style={{ fontSize: '2.5rem', fontWeight: 800, color: theme.primary, marginBottom: '20px' }}>
                        Standardize Your Stack
                    </h2>
                    <p style={{ fontSize: '1.2rem', color: '#666', maxWidth: '800px', margin: '0 auto 40px', lineHeight: 1.6 }}>
                        Fragmented tools create chaos. <strong>arcee</strong> unifies your application development on a single, scalable standard.
                    </p>

                    {/* VISUALIZATION */}
                    <div className="d-flex justify-content-center mb-5"
                        style={{
                            background: '#fff',
                            borderRadius: '24px',
                            padding: '50px 20px',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.03)',
                            border: '1px solid #eee'
                        }}>
                        <StandardizationVisual primaryColor={theme.primary} secondaryColor={theme.secondary} />
                    </div>
                </div>

                <div className="row g-4">
                    {features.map((feature, index) => (
                        <div key={index} className="col-md-4">
                            <div
                                style={{
                                    padding: '40px 32px',
                                    background: '#fff',
                                    borderRadius: '16px',
                                    textAlign: 'center',
                                    height: '100%',
                                    // boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                                    border: '1px solid #eaeaea',
                                    transition: 'transform 0.2s, box-shadow 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-5px)';
                                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.08)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                    e.currentTarget.style.border = '1px solid #eaeaea';
                                }}
                            >
                                <div style={{
                                    width: '64px',
                                    height: '64px',
                                    borderRadius: '50%',
                                    background: `${theme.primary}10`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 24px'
                                }}>
                                    <i
                                        className={`${feature.icon}`}
                                        style={{
                                            fontSize: '1.75rem',
                                            color: theme.primary,
                                        }}
                                    ></i>
                                </div>
                                <h5 style={{ fontWeight: 700, color: '#333', marginBottom: '12px' }}>
                                    {feature.title}
                                </h5>
                                <p style={{ color: '#666', margin: 0, lineHeight: 1.6 }}>
                                    {feature.description}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export default StandardizeSection;
